/**
 * src/lib/services/verification.ts
 * Contact verification (SMS or email) - talks ONLY to auth-service via the gateway.
 * The OTP Service and its credentials never reach the browser.
 *
 * Uses the shared client (HttpOnly cookie, X-Requested-With header, 401 refresh).
 * That client only surfaces string `detail` messages, so behaviour is keyed off
 * the HTTP status: 400 wrong/expired/no code, 409 already verified, 429 cooldown
 * or too many attempts, 502 delivery failed, 503 SMS unavailable (out of credits
 * or gateway down - offer email instead).
 */

import { ApiError, post, put } from '@/lib/api/client';

export type VerificationChannel = 'sms' | 'email';

export interface RequestCodeResult {
  channel: VerificationChannel;
  sent_to: string;
  resend_after_seconds: number;
}

export type VerificationFailure =
  | 'sms_unavailable'
  | 'cooldown'
  | 'already_verified'
  | 'delivery_failed'
  | 'bad_code'
  | 'other';

export function classifyVerificationError(err: unknown): { kind: VerificationFailure; message: string } {
  if (!(err instanceof ApiError)) {
    return { kind: 'other', message: 'Network problem. Please try again.' };
  }
  const kind: VerificationFailure =
    err.status === 503
      ? 'sms_unavailable'
      : err.status === 409
        ? 'already_verified'
        : err.status === 502
          ? 'delivery_failed'
          : err.status === 429
            ? 'cooldown'
            : err.status === 400
              ? 'bad_code'
              : 'other';
  return { kind, message: err.message };
}

export const requestVerificationCode = (channel: VerificationChannel) =>
  post<RequestCodeResult>('/api/v1/auth/verification/request', { channel });

export const confirmVerificationCode = (channel: VerificationChannel, code: string) =>
  post<{ channel: VerificationChannel; verified: boolean }>('/api/v1/auth/verification/confirm', {
    channel,
    code,
  });

export const setVerificationPreference = (method: VerificationChannel) =>
  put<{ method: VerificationChannel }>('/api/v1/auth/verification/preference', { method });
